import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  BrainCircuit,
  CalendarCheck,
  CheckCircle2,
  Database,
  FileSearch,
  Lock,
  Scale,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';

export interface GouvernanceAgentiqueV2ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const foundations = [
  {
    title: 'NIST AI RMF 1.0',
    body: 'Structure de gouvernance, cartographie, mesure et gestion des risques IA.',
  },
  {
    title: 'NIST AI 600-1',
    body: 'Profil IA générative : hallucinations, contenu, données sensibles et confiance.',
  },
  {
    title: 'NIST Cybersecurity Framework',
    body: 'Contrôles cyber, accès, protection des données financières et surveillance.',
  },
  {
    title: 'ISO/IEC 42001',
    body: 'Système de management IA : politiques, responsabilités, audits et amélioration continue.',
  },
  {
    title: 'Loi 25',
    body: 'Protection des renseignements personnels, consentement, conservation et traçabilité.',
  },
  {
    title: 'OWASP LLM / Agentic Security',
    body: 'Contrôles pratiques contre prompt injection, abus d’outils et chaînes agentiques.',
  },
];

const accountingRisks = [
  'Données financières de clients, déclarations fiscales, états financiers et dossiers de paie',
  'Confidentialité professionnelle et séparation stricte entre dossiers clients',
  'Exactitude des calculs, sources et recommandations avant toute décision fiscale ou comptable',
  'Validation humaine obligatoire pour les livrables remis aux clients ou aux autorités',
  'Traçabilité complète des requêtes, sources, versions et approbations',
  'Encadrement des outils IA utilisés pour recherche, analyse, rédaction et automatisation',
];

const pillars = [
  {
    icon: ShieldCheck,
    title: 'Gouvernance & imputabilité',
    body: 'Sponsor, responsable BZ, owner client, règles d’usage, registres des agents et critères d’approbation.',
  },
  {
    icon: Lock,
    title: 'Accès & confidentialité',
    body: 'Moindre privilège, cloisonnement par client, contrôle des données sensibles et révocation rapide.',
  },
  {
    icon: Database,
    title: 'RAG contrôlé',
    body: 'Corpus approuvés, sources citées, versionnement, exclusion des données non autorisées et revue des réponses.',
  },
  {
    icon: BrainCircuit,
    title: 'Agents spécialisés',
    body: 'Agents limités par mandat : recherche, synthèse, brouillon, contrôle qualité, jamais décision autonome critique.',
  },
  {
    icon: Users,
    title: 'Human-in-the-loop',
    body: 'Validation professionnelle avant envoi, signature, dépôt, recommandation financière ou action irréversible.',
  },
  {
    icon: FileSearch,
    title: 'Audit & preuves',
    body: 'Journaux exploitables, justification des sources, registre des décisions et revue périodique des écarts.',
  },
];

const roadmap = [
  {
    period: '0-30 jours',
    title: 'Cadrage et règles de base',
    items: ['Registre des usages IA', 'Règles d’accès et données interdites', 'RACI initial avec Eric Hains comme responsable BZ'],
  },
  {
    period: '30-60 jours',
    title: 'RAG et premiers agents contrôlés',
    items: ['Corpus pilote', 'Agent de recherche/synthèse', 'Agent de brouillon avec validation humaine'],
  },
  {
    period: '60-90 jours',
    title: 'Contrôles et essais',
    items: ['Tests hallucination/source', 'Journalisation', 'Seuils d’escalade et critères de rejet'],
  },
  {
    period: '3-6 mois',
    title: 'Industrialisation prudente',
    items: ['Tableau de bord de conformité', 'Revue mensuelle', 'Extension à 2-3 processus comptables à faible risque'],
  },
  {
    period: '6-12 mois',
    title: 'Gouvernance vivante',
    items: ['Audit périodique', 'Mesure ROI/risque', 'Amélioration continue et mise à jour des politiques'],
  },
];

const raciRows = [
  ['Cadrage du mandat IA', 'Eric Hains', 'Direction client', 'TI / sécurité', 'Utilisateurs clés'],
  ['Approbation des usages IA', 'Direction client', 'Direction client', 'Eric Hains', 'Équipe comptable'],
  ['Accès aux données clients', 'Responsable données client', 'Direction client', 'Eric Hains / TI', 'Utilisateurs autorisés'],
  ['Validation des livrables IA', 'Professionnel responsable', 'Direction client', 'Eric Hains', 'Client final au besoin'],
  ['Revue des incidents / écarts', 'Eric Hains', 'Direction client', 'Sécurité / conformité', 'Équipes touchées'],
];

export default function GouvernanceAgentiqueV2Modal({ isOpen, onClose }: GouvernanceAgentiqueV2ModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-slate-950 shadow-2xl"
          >
            <header className="relative border-b border-white/10 bg-linear-to-r from-slate-950 via-indigo-950 to-slate-950 px-6 py-6 md:px-8">
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">
                Projet PRIsME · Adaptation client comptable
              </p>
              <div className="max-w-4xl">
                <h2 className="text-2xl font-black text-white md:text-4xl">Gouvernance Agentique V2</h2>
                <p className="mt-3 text-sm leading-7 text-slate-200 md:text-base">
                  Plan de gouvernance IA enrichi avec la gouvernance agentique BZ et spécialisé pour un client
                  comptable : données financières, confidentialité, validation professionnelle, RAG contrôlé et agents
                  IA supervisés.
                </p>
              </div>
            </header>

            <main className="flex-1 space-y-7 overflow-y-auto px-5 py-6 md:px-8">
              <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="rounded-3xl border border-indigo-400/20 bg-indigo-500/10 p-5">
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                    <ShieldCheck className="h-5 w-5 text-indigo-200" />
                    Positionnement
                  </h3>
                  <p className="text-sm leading-7 text-slate-200">
                    Le mandat vise à créer une gouvernance agentique utilisable par un cabinet comptable avant de
                    déployer des assistants, du RAG ou des agents spécialisés. L’objectif n’est pas de remplacer le
                    jugement professionnel, mais de rendre l’IA encadrée, mesurable, traçable et approuvée.
                  </p>
                </div>
                <div className="rounded-3xl border border-emerald-400/20 bg-emerald-500/10 p-5">
                  <h3 className="mb-3 text-lg font-bold text-emerald-100">Responsabilité BZ</h3>
                  <div className="space-y-3 text-sm text-slate-200">
                    <p>
                      <span className="font-bold text-white">Responsable BZ :</span> Eric Hains
                    </p>
                    <p>
                      <span className="font-bold text-white">Rôle :</span> cadrage, gouvernance, priorisation,
                      validation client et coordination des décisions.
                    </p>
                    <p>
                      <span className="font-bold text-white">Principe :</span> aucune autonomie élargie sans
                      approbation formelle et preuve de contrôle.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
                  <Scale className="h-5 w-5 text-emerald-200" />
                  Fondations de référence
                </h3>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {foundations.map((item) => (
                    <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="mb-1 font-bold text-emerald-100">{item.title}</p>
                      <p className="text-xs leading-5 text-slate-300">{item.body}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-3xl border border-amber-400/20 bg-amber-500/10 p-5">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-amber-100">
                  <AlertTriangle className="h-5 w-5" />
                  Risques et réalités d’un client comptable
                </h3>
                <div className="grid gap-2 md:grid-cols-2">
                  {accountingRisks.map((risk) => (
                    <div key={risk} className="flex gap-2 text-sm leading-6 text-slate-200">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-amber-200" />
                      <span>{risk}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-lg font-bold text-white">Architecture de gouvernance proposée</h3>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {pillars.map((pillar) => {
                    const Icon = pillar.icon;
                    return (
                      <div key={pillar.title} className="rounded-2xl border border-white/10 bg-slate-900/70 p-4">
                        <Icon className="mb-3 h-7 w-7 text-indigo-200" />
                        <p className="mb-2 font-bold text-white">{pillar.title}</p>
                        <p className="text-xs leading-5 text-slate-300">{pillar.body}</p>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-3xl border border-cyan-400/20 bg-cyan-500/10 p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-cyan-100">
                    <CalendarCheck className="h-5 w-5" />
                    Feuille de route 12 mois
                  </h3>
                  <div className="space-y-3">
                    {roadmap.map((step) => (
                      <div key={step.period} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-cyan-200">{step.period}</p>
                        <p className="mt-1 font-bold text-white">{step.title}</p>
                        <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-300">
                          {step.items.map((item) => (
                            <li key={item} className="flex gap-2">
                              <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-200" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-white">
                    <Users className="h-5 w-5 text-indigo-200" />
                    RACI simplifié
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left text-xs text-slate-200">
                      <thead>
                        <tr className="border-b border-white/10 text-slate-400">
                          <th className="py-2 pr-3">Activité</th>
                          <th className="px-3 py-2">R</th>
                          <th className="px-3 py-2">A</th>
                          <th className="px-3 py-2">C</th>
                          <th className="px-3 py-2">I</th>
                        </tr>
                      </thead>
                      <tbody>
                        {raciRows.map(([activity, responsible, accountable, consulted, informed]) => (
                          <tr key={activity} className="border-b border-white/5">
                            <td className="py-3 pr-3 font-semibold text-white">{activity}</td>
                            <td className="px-3 py-3">{responsible}</td>
                            <td className="px-3 py-3">{accountable}</td>
                            <td className="px-3 py-3">{consulted}</td>
                            <td className="px-3 py-3">{informed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </main>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
